// Budget range conversion with smart rounding for different currencies

import { convertCurrency } from './currencyUtils';

// Define base budget ranges in USD (source of truth)
const BASE_BUDGET_RANGES_USD = {
  under25: { min: 0, max: 25 },
  '25-50': { min: 25, max: 50 },
  '50-100': { min: 50, max: 100 },
  '100-200': { min: 100, max: 200 },
  '200plus': { min: 200, max: null }
};

// Smart rounding function - rounds UP to nice round numbers
// Makes budget ranges feel more generous and psychologically appealing
const smartRound = (value, currency) => {
  // For zero-decimal currencies (JPY, KRW, IDR, CLP)
  if (['JPY', 'KRW', 'IDR', 'CLP'].includes(currency)) {
    if (value < 100) return Math.ceil(value / 10) * 10;       // Round up to 10s
    if (value < 1000) return Math.ceil(value / 100) * 100;    // Round up to 100s
    if (value < 10000) return Math.ceil(value / 500) * 500;   // Round up to 500s
    return Math.ceil(value / 1000) * 1000;                    // Round up to 1000s
  }

  // For regular decimal currencies
  if (value < 10) return Math.ceil(value);                    // Round up to 1s
  if (value < 50) return Math.ceil(value / 10) * 10;          // Round up to 10s
  if (value < 200) return Math.ceil(value / 25) * 25;         // Round up to 25s
  if (value < 500) return Math.ceil(value / 50) * 50;         // Round up to 50s
  if (value < 2000) return Math.ceil(value / 100) * 100;      // Round up to 100s
  return Math.ceil(value / 500) * 500;                        // Round up to 500s
};

// Convert budget range to target currency with smart rounding
export const convertBudgetRange = (rangeKey, targetCurrency) => {
  const baseRange = BASE_BUDGET_RANGES_USD[rangeKey];
  if (!baseRange) return null;

  const convertedMin = convertCurrency(baseRange.min, 'USD', targetCurrency);
  const convertedMax = baseRange.max
    ? convertCurrency(baseRange.max, 'USD', targetCurrency)
    : null;

  return {
    min: smartRound(convertedMin, targetCurrency),
    max: convertedMax ? smartRound(convertedMax, targetCurrency) : null
  };
};

// Generate display label for budget option
export const getBudgetLabel = (rangeKey, currency, currencySymbol) => {
  if (rangeKey === 'custom') {
    return 'Custom range...';
  }

  const range = convertBudgetRange(rangeKey, currency);
  if (!range) return '';

  if (rangeKey === 'under25') {
    return `Under ${currencySymbol}${range.max}`;
  } else if (rangeKey === '200plus') {
    return `${currencySymbol}${range.min}+`;
  } else {
    return `${currencySymbol}${range.min} - ${currencySymbol}${range.max}`;
  }
};
